import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { FileCheck, CheckCircle, XCircle, Eye, AlertTriangle, Clock, UserPlus, User, MapPin, Briefcase, GraduationCap, FileText, ClipboardCheck, Download, ExternalLink, Image } from 'lucide-react';
import AuditTrail from '@/components/shared/AuditTrail';
import { toast } from 'sonner';
import axios from 'axios';
import CheckDetailsView from '@/components/checks/CheckDetailsView';
import { formatDateDDMonYYYY } from '@/utils/textFormatting';

const CHECK_NAMES = {
  'address_check': 'Address Verification',
  'criminal_court_check': 'Criminal Court Check',
  'court_advocate_check': 'Court Advocate Check',
  'criminal_advocate_check': 'Criminal Advocate Check',
  'id_check': 'ID Verification',
  'employment_check': 'Employment Verification',
  'education_check': 'Education Verification',
  'credit_check': 'Credit Check',
  'drug_test': 'Drug Test',
  'reference_check': 'Reference Check',
  'personal_reference': 'Personal Reference',
  'professional_reference': 'Professional Reference',
  'global_database': 'Global Database Check',
  'world_criminal_db': 'World Criminal Database',
  'india_criminal_db': 'India Criminal Database'
};

// Helper: Normalize employment entries from all possible data sources and field names
const normalizeEmploymentEntries = (employee) => {
  if (!employee) return [];
  
  // Build entries from all sources: latest_employment + past_employments + employment_entries
  let entries = [];
  
  // Prefer latest_employment + past_employments (from data entry form) if available
  if (employee.latest_employment) {
    entries = [employee.latest_employment, ...(employee.past_employments || [])];
  } else if (employee.employment_entries?.length > 0) {
    entries = [...employee.employment_entries];
  } else if (employee.past_employments?.length > 0) {
    entries = [...employee.past_employments];
  } else if (employee.employment_history?.length > 0) {
    entries = [...employee.employment_history];
  }
  
  // Normalize field names so QC display always finds them
  return entries.map(emp => ({
    ...emp,
    company_name: emp.company_name || emp.employer_name || '',
    employee_id: emp.employee_id || emp.employee_code || '',
    ctc: emp.ctc || emp.salary || '',
    designation: emp.designation || '',
    start_date: emp.start_date || emp.date_of_joining || emp.employed_from || '',
    end_date: emp.end_date || emp.date_of_relieving || emp.employed_to || '',
    work_location: emp.work_location || emp.location || '',
    is_current: emp.is_current || emp.employment_type === 'current' || false,
    // HR details
    hr_name: emp.hr_name || '',
    hr_email: emp.hr_email || '',
    hr_phone: emp.hr_phone || '',
    // Reporting Manager details
    reporting_manager_name: emp.reporting_manager_name || emp.rm_name || '',
    reporting_manager_email: emp.reporting_manager_email || emp.rm_email || '',
    reporting_manager_phone: emp.reporting_manager_phone || emp.rm_contact || '',
    reporting_manager_designation: emp.reporting_manager_designation || emp.rm_designation || '',
    reporting_manager_department: emp.reporting_manager_department || emp.rm_department || '',
    // Extra fields
    uan_pf_number: emp.uan_pf_number || '',
    reimbursements: emp.reimbursements || '',
  }));
};

// Helper: Normalize education entries from all possible data sources
const normalizeEducationEntries = (employee) => {
  if (!employee) return [];
  
  let entries = [];
  
  // Prefer highest_education + prior_educations (from data entry form) if available
  if (employee.highest_education) {
    entries = [employee.highest_education, ...(employee.prior_educations || [])];
  } else if (employee.education_entries?.length > 0) {
    entries = [...employee.education_entries];
  } else if (employee.education_details?.length > 0) {
    entries = [...employee.education_details];
  }
  
  return entries.map(edu => ({
    ...edu,
    institution_name: edu.institution_name || edu.college_name || edu.college || '',
    university: edu.university || edu.university_name || '',
    course: edu.course || edu.course_name || edu.degree || '',
    qualification: edu.qualification || edu.education_type || '',
    year_of_passing: edu.year_of_passing || '',
    roll_number: edu.roll_number || edu.roll_no || '',
    grade: edu.grade || edu.percentage || edu.class_degree || '',
    specialization: edu.specialization || '',
    name_as_per_doc: edu.name_as_per_doc || '',
  }));
};

// Helper function to format address from different data structures
const formatAddress = (employee, addressType = 'present') => {
  // Check for nested address object (new format: present_address, permanent_address)
  const addressObj = employee?.[`${addressType}_address`];
  if (addressObj && typeof addressObj === 'object') {
    const parts = [
      addressObj.complete_address || addressObj.house_no,
      addressObj.landmark,
      addressObj.area,
      addressObj.city,
      addressObj.state,
      addressObj.pincode
    ].filter(Boolean);
    return parts.length > 0 ? parts : null;
  }
  
  // Fallback to flat fields (old format: present_house_no, present_city, etc.)
  const parts = [
    employee?.[`${addressType}_house_no`],
    employee?.[`${addressType}_cross`],
    employee?.[`${addressType}_main`],
    employee?.[`${addressType}_area`],
    employee?.[`${addressType}_city`],
    employee?.[`${addressType}_state`],
    employee?.[`${addressType}_pincode`]
  ].filter(Boolean);
  return parts.length > 0 ? parts : null;
};

function QCCasesTab() {
  const [cases, setCases] = React.useState([]);
  const [employees, setEmployees] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [viewCase, setViewCase] = React.useState(null);
  const [viewEmployee, setViewEmployee] = React.useState(null);
  const [selectedCheck, setSelectedCheck] = React.useState(null);
  const [agents, setAgents] = React.useState([]);
  const [showInsuffDialog, setShowInsuffDialog] = React.useState(null);
  const [showApproveDialog, setShowApproveDialog] = React.useState(null);
  const [insuffForm, setInsuffForm] = React.useState({ reason: '', description: '' });
  const [approveForm, setApproveForm] = React.useState({ assignTo: '', bypassInsuff: false });
  const [insufficiencies, setInsufficiencies] = React.useState({});
  const [activeReviewTab, setActiveReviewTab] = React.useState('personal');
  const [checkDocuments, setCheckDocuments] = React.useState({});
  const [loadingDocuments, setLoadingDocuments] = React.useState(false);
  const [previewDocument, setPreviewDocument] = React.useState(null);
  const [entryInsufficiencies, setEntryInsufficiencies] = React.useState([]);
  
  // Bulk selection state
  const [selectedChecks, setSelectedChecks] = React.useState({});  // {caseId: [checkType1, checkType2]}
  const [showBulkUpdateDialog, setShowBulkUpdateDialog] = React.useState(null);
  const [bulkUpdateStatus, setBulkUpdateStatus] = React.useState('verified');
  const [bulkUpdateRemarks, setBulkUpdateRemarks] = React.useState('');
  const [bulkUpdating, setBulkUpdating] = React.useState(false);

  // Bulk case submit state (for submitting multiple cases at once)
  const [selectedCaseIds, setSelectedCaseIds] = React.useState(new Set());
  const [showBulkSubmitDialog, setShowBulkSubmitDialog] = React.useState(false);
  const [bulkSubmitting, setBulkSubmitting] = React.useState(false);

  // State to track expanded case (show detail view inline)
  const [expandedCaseId, setExpandedCaseId] = React.useState(null);

  React.useEffect(() => {
    loadData();
    loadAgents();
  }, []);

  const loadData = async () => {
    try {
      const [casesRes, employeesRes] = await Promise.all([
        axios.get('/cases'),
        axios.get('/employees')
      ]);
      
      const pendingCases = casesRes.data.filter(c => c.status?.toLowerCase() === 'pending_qc');
      setCases(pendingCases);
      setEmployees(employeesRes.data);
      
      // Load insufficiencies for all cases
      const insuffRes = await axios.get('/insufficiencies');
      const insuffMap = {};
      for (const insuff of insuffRes.data) {
        const key = `${insuff.case_id}_${insuff.check_id}`;
        if (!insuffMap[key]) insuffMap[key] = [];
        insuffMap[key].push(insuff);
      }
      setInsufficiencies(insuffMap);
    } catch (error) {
      toast.error('Failed to load cases');
    } finally {
      setLoading(false);
    }
  };

  const loadAgents = async () => {
    try {
      // Fetch all users including inactive, then filter to only active verification_team members
      const res = await axios.get('/users?role=verification_team&include_inactive=true');
      // Filter to only show active agents
      const activeAgents = (res.data || []).filter(agent => agent.is_active !== false);
      setAgents(activeAgents);
    } catch (e) {
      console.log('Could not load agents');
    }
  };

  const getEmployeeName = (employeeId) => {
    const emp = employees.find(e => e.id === employeeId);
    return emp ? emp.name : 'Unknown';
  };

  const getEmployee = (employeeId) => {
    return employees.find(e => e.id === employeeId);
  };

  const loadCheckDocuments = async (caseItem, employeeId) => {
    setLoadingDocuments(true);
    const docsMap = {};
    try {
      // Load documents for each check
      for (const check of (caseItem.checks || [])) {
        try {
          const res = await axios.get(`/cases/${caseItem.id}/checks/${check.check_id}/details`);
          docsMap[check.check_id] = res.data?.documents || [];
        } catch {
          docsMap[check.check_id] = [];
        }
      }
      
      // Also load employee documents directly
      if (employeeId) {
        try {
          const empDocsRes = await axios.get(`/documents?employee_id=${employeeId}`);
          docsMap['employee_docs'] = empDocsRes.data || [];
        } catch {
          docsMap['employee_docs'] = [];
        }
      }
      
      setCheckDocuments(docsMap);
    } catch (error) {
      console.error('Failed to load check documents:', error);
    } finally {
      setLoadingDocuments(false);
    }
  };

  // Normalize employee data to ensure consistent field names for display
  const normalizeEmployee = (emp) => {
    if (!emp) return emp;
    return {
      ...emp,
      employment_entries: normalizeEmploymentEntries(emp),
      education_entries: normalizeEducationEntries(emp),
    };
  };

  const openViewCase = async (caseItem) => {
    setViewCase(caseItem);
    setActiveReviewTab('personal');
    const emp = getEmployee(caseItem.employee_id);
    if (emp) {
      try {
        const r = await axios.get(`/employees/${emp.id}`);
        setViewEmployee(normalizeEmployee(r.data));
      } catch {
        setViewEmployee(normalizeEmployee(emp));
      }
    }
    // Load documents for all checks and employee documents
    loadCheckDocuments(caseItem, caseItem.employee_id);
    // Load entry insufficiencies for this case
    setEntryInsufficiencies(caseItem.entry_insufficiencies || []);
  };

  const openCheckDetails = (caseItem, check) => {
    setSelectedCheck({ caseId: caseItem.id, checkId: check.check_id, check });
    setViewCase(null);
  };

  const getCheckInsuffs = (caseId, checkId) => {
    const key = `${caseId}_${checkId}`;
    return insufficiencies[key] || [];
  };

  const hasOpenInsuff = (caseId, checkId) => {
    const insuffs = getCheckInsuffs(caseId, checkId);
    return insuffs.some(i => i.status === 'open' || i.status === 'waiting_for_clearance');
  };

  const getInsuffStatus = (caseId, checkId) => {
    const insuffs = getCheckInsuffs(caseId, checkId);
    const waiting = insuffs.find(i => i.status === 'waiting_for_clearance');
    const open = insuffs.find(i => i.status === 'open');
    if (waiting) return 'waiting';
    if (open) return 'open';
    return null;
  };

  const handleRaiseInsufficiency = async () => {
    if (!insuffForm.reason || !insuffForm.description) {
      toast.error('Please fill all fields');
      return;
    }
    try {
      const { caseId, checkId } = showInsuffDialog;
      await axios.post(`/cases/${caseId}/checks/${checkId}/insufficiency?reason=${encodeURIComponent(insuffForm.reason)}&description=${encodeURIComponent(insuffForm.description)}`);
      toast.success('Insufficiency raised successfully');
      setShowInsuffDialog(null);
      setInsuffForm({ reason: '', description: '' });
      // Refresh without resetting tab
      const currentCaseId = viewCase?.id;
      await loadData();
      if (currentCaseId) {
        const updated = cases.find(c => c.id === currentCaseId);
        if (updated) setViewCase(updated);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to raise insufficiency');
    }
  };

  const handleClearInsufficiency = async (caseId, checkId) => {
    try {
      await axios.put(`/cases/${caseId}/checks/${checkId}/clear-insufficiency`);
      toast.success('Insufficiency cleared');
      const currentCaseId = viewCase?.id;
      await loadData();
      if (currentCaseId) {
        const updated = cases.find(c => c.id === currentCaseId);
        if (updated) setViewCase(updated);
      }
    } catch (error) {
      toast.error('Failed to clear insufficiency');
    }
  };

  const handleApproveCheck = async () => {
    try {
      const { caseId, checkId, hasInsuff } = showApproveDialog;
      let url = `/cases/${caseId}/checks/${checkId}/approve?bypass_insufficiency=${approveForm.bypassInsuff || !hasInsuff}`;
      if (approveForm.assignTo) {
        url += `&assign_to=${approveForm.assignTo}`;
      }
      await axios.put(url);
      toast.success('Check approved and assigned');
      setShowApproveDialog(null);
      setApproveForm({ assignTo: '', bypassInsuff: false });
      
      // Refresh data and update viewCase WITHOUT resetting the active tab
      const [casesRes, employeesRes] = await Promise.all([
        axios.get('/cases'),
        axios.get('/employees')
      ]);
      const pendingCases = casesRes.data.filter(c => c.status?.toLowerCase() === 'pending_qc');
      setCases(pendingCases);
      setEmployees(employeesRes.data);
      
      // Reload insufficiencies
      const insuffRes = await axios.get('/insufficiencies');
      const insuffMap = {};
      for (const insuff of insuffRes.data) {
        const key = `${insuff.case_id}_${insuff.check_id}`;
        if (!insuffMap[key]) insuffMap[key] = [];
        insuffMap[key].push(insuff);
      }
      setInsufficiencies(insuffMap);
      
      // Update viewCase with fresh data (keeps dialog open on same tab)
      const updatedCase = pendingCases.find(c => c.id === caseId);
      if (updatedCase) {
        setViewCase(updatedCase);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve check');
    }
  };

  const handleAutoApproveAll = async (caseItem) => {
    const checksWithoutInsuff = caseItem.checks?.filter(c => !hasOpenInsuff(caseItem.id, c.check_id)) || [];
    if (checksWithoutInsuff.length === 0) {
      toast.error('All checks have insufficiencies');
      return;
    }
    
    try {
      for (const check of checksWithoutInsuff) {
        await axios.put(`/cases/${caseItem.id}/checks/${check.check_id}/approve?bypass_insufficiency=false`);
      }
      toast.success(`${checksWithoutInsuff.length} checks auto-approved`);
      loadData();
    } catch (error) {
      toast.error('Failed to auto-approve checks');
    }
  };

  // Bulk selection helper functions
  const toggleCheckSelection = (caseId, checkType) => {
    setSelectedChecks(prev => {
      const caseChecks = prev[caseId] || [];
      const isSelected = caseChecks.includes(checkType);
      
      if (isSelected) {
        return {
          ...prev,
          [caseId]: caseChecks.filter(c => c !== checkType)
        };
      } else {
        return {
          ...prev,
          [caseId]: [...caseChecks, checkType]
        };
      }
    });
  };

  const selectAllChecks = (caseItem) => {
    const eligibleChecks = caseItem.checks?.filter(c => {
      const insuffStatus = getInsuffStatus(caseItem.id, c.check_id);
      return insuffStatus !== 'open' && insuffStatus !== 'waiting';
    }) || [];
    
    const checkTypes = eligibleChecks.map(c => {
      const checkType = c.check_type;
      return typeof checkType === 'object' ? checkType.check_type : checkType;
    });
    
    setSelectedChecks(prev => ({
      ...prev,
      [caseItem.id]: checkTypes
    }));
  };

  const clearAllSelections = (caseId) => {
    setSelectedChecks(prev => ({
      ...prev,
      [caseId]: []
    }));
  };

  const getSelectedCount = (caseId) => {
    return selectedChecks[caseId]?.length || 0;
  };

  // Bulk case selection handlers
  const toggleCaseSelection = (caseId) => {
    setSelectedCaseIds(prev => {
      const next = new Set(prev);
      if (next.has(caseId)) next.delete(caseId);
      else next.add(caseId);
      return next;
    });
  };

  const toggleAllCases = () => {
    if (selectedCaseIds.size === cases.length) {
      setSelectedCaseIds(new Set());
    } else {
      setSelectedCaseIds(new Set(cases.map(c => c.id)));
    }
  };

  const handleBulkSubmitCases = async () => {
    if (selectedCaseIds.size === 0) {
      toast.error('No cases selected');
      return;
    }
    setBulkSubmitting(true);
    try {
      const response = await axios.post('/qc/bulk-submit', {
        case_ids: Array.from(selectedCaseIds)
      });
      toast.success(response.data.message || `${response.data.submitted} cases submitted`);
      if (response.data.errors?.length > 0) {
        toast.warning(`${response.data.errors.length} cases had errors`);
      }
      setSelectedCaseIds(new Set());
      setShowBulkSubmitDialog(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to bulk submit cases');
    } finally {
      setBulkSubmitting(false);
    }
  };

  const isCheckSelected = (caseId, checkType) => {
    const actualCheckType = typeof checkType === 'object' ? checkType.check_type : checkType;
    return selectedChecks[caseId]?.includes(actualCheckType) || false;
  };

  const handleBulkStatusUpdate = async (caseId) => {
    const checkTypes = selectedChecks[caseId] || [];
    if (checkTypes.length === 0) {
      toast.error('No checks selected');
      return;
    }

    setBulkUpdating(true);
    try {
      const response = await axios.put(`/cases/${caseId}/checks/bulk`, {
        check_types: checkTypes,
        status: bulkUpdateStatus,
        remarks: bulkUpdateRemarks || `Bulk updated by QC on ${new Date().toLocaleDateString()}`
      });
      
      toast.success(response.data.message || `${checkTypes.length} checks updated to ${bulkUpdateStatus}`);
      setShowBulkUpdateDialog(null);
      setBulkUpdateStatus('verified');
      setBulkUpdateRemarks('');
      clearAllSelections(caseId);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to bulk update checks');
    } finally {
      setBulkUpdating(false);
    }
  };

  // Handle clearing entry-level insufficiency
  const handleClearEntryInsufficiency = async (entryType, entryIndex, fieldName) => {
    try {
      await axios.put(`/cases/${viewCase.id}/entry-insufficiency/clear`, {
        entry_type: entryType,
        entry_index: entryIndex,
        field_name: fieldName
      });
      toast.success('Insufficiency cleared');
      // Update local state
      setEntryInsufficiencies(prev => 
        prev.map(i => 
          i.entry_type === entryType && i.entry_index === entryIndex && i.field_name === fieldName
            ? { ...i, status: 'cleared' }
            : i
        )
      );
      loadData();
    } catch (error) {
      toast.error('Failed to clear insufficiency');
    }
  };

  // Detect missing data in employee record
  const detectMissingData = (employee) => {
    const issues = [];
    
    // Personal info checks
    if (!employee?.pan_number) issues.push({ section: 'Personal', field: 'PAN Number', status: 'missing' });
    if (!employee?.aadhar_number) issues.push({ section: 'Personal', field: 'Aadhar Number', status: 'missing' });
    if (!employee?.alternate_phone && !employee?.alternate_contact) issues.push({ section: 'Personal', field: 'Alternate Contact', status: 'missing' });
    if (!employee?.gender) issues.push({ section: 'Personal', field: 'Gender', status: 'missing' });
    
    // Present address checks
    if (!employee?.present_house_no && !employee?.present_address?.house_no) issues.push({ section: 'Present Address', field: 'House Number', status: 'missing' });
    if (!employee?.present_city && !employee?.present_address?.city) issues.push({ section: 'Present Address', field: 'City', status: 'missing' });
    if (!employee?.present_pincode && !employee?.present_address?.pincode) issues.push({ section: 'Present Address', field: 'Pincode', status: 'missing' });
    
    // Permanent address checks
    const hasPermanent = employee?.permanent_house_no || employee?.permanent_address?.house_no;
    if (!hasPermanent && !employee?.permanent_same_as_present) issues.push({ section: 'Permanent Address', field: 'Complete Address', status: 'missing' });
    
    // Employment checks
    if (employee?.employment_entries?.length > 0) {
      employee.employment_entries.forEach((emp, idx) => {
        if (!emp.employee_id) issues.push({ section: `Employment #${idx + 1}`, field: 'Employee ID', status: 'missing' });
        if (!emp.ctc) issues.push({ section: `Employment #${idx + 1}`, field: 'CTC/Salary', status: 'missing' });
        if (!emp.reporting_manager_name && !emp.reporting_manager_email) {
          issues.push({ section: `Employment #${idx + 1}`, field: 'Reporting Manager', status: 'missing' });
        }
      });
    } else if (employee?.employee_category === 'experienced' || (!employee?.employee_category && !employee?.is_fresher)) {
      // Only flag missing employment for experienced category
      issues.push({ section: 'Employment', field: 'Employment History', status: 'missing' });
    }
    
    // Education checks
    if (employee?.education_entries?.length > 0) {
      employee.education_entries.forEach((edu, idx) => {
        if (!edu.year_of_passing) issues.push({ section: `Education #${idx + 1}`, field: 'Year of Passing', status: 'missing' });
        if (!edu.roll_number) issues.push({ section: `Education #${idx + 1}`, field: 'Roll Number', status: 'missing' });
      });
    } else {
      issues.push({ section: 'Education', field: 'Education History', status: 'missing' });
    }
    
    // References
    if (!employee?.personal_references || employee.personal_references.length === 0) {
      issues.push({ section: 'References', field: 'Personal References', status: 'missing' });
    }
    if (!employee?.professional_references || employee.professional_references.length === 0) {
      issues.push({ section: 'References', field: 'Professional References', status: 'missing' });
    }
    
    return issues;
  };

  // Detect missing documents based on checks
  const detectMissingDocuments = (caseItem, employee, documents) => {
    const issues = [];
    const allDocs = documents || [];
    
    // Helper to check if document type exists
    const hasDocument = (docType) => {
      return allDocs.some(d => 
        d.document_type?.toLowerCase().includes(docType.toLowerCase()) ||
        d.file_name?.toLowerCase().includes(docType.toLowerCase())
      );
    };
    
    // Required documents based on checks
    const checks = caseItem?.checks || [];
    
    // ID Documents - Always required
    if (!hasDocument('pan') && !hasDocument('pan_card')) {
      issues.push({ section: 'Documents', field: 'PAN Card', status: 'missing', docType: 'pan_card' });
    }
    if (!hasDocument('aadhar') && !hasDocument('aadhaar')) {
      issues.push({ section: 'Documents', field: 'Aadhar Card', status: 'missing', docType: 'aadhar_card' });
    }
    if (!hasDocument('photo') && !hasDocument('passport_photo')) {
      issues.push({ section: 'Documents', field: 'Passport Photo', status: 'missing', docType: 'photo' });
    }
    
    // Address proof
    const hasAddressCheck = checks.some(c => c.check_type === 'address_check');
    if (hasAddressCheck && !hasDocument('address_proof') && !hasDocument('utility_bill') && !hasDocument('rent_agreement')) {
      issues.push({ section: 'Documents', field: 'Address Proof', status: 'missing', docType: 'address_proof' });
    }
    
    // Employment documents
    const hasEmploymentCheck = checks.some(c => c.check_type === 'employment_check');
    if (hasEmploymentCheck && employee?.employment_entries?.length > 0) {
      employee.employment_entries.forEach((emp, idx) => {
        const hasRelievingLetter = allDocs.some(d => 
          (d.document_type?.toLowerCase().includes('relieving') || d.file_name?.toLowerCase().includes('relieving')) &&
          (d.description?.includes(`${idx}`) || d.employment_index === idx || allDocs.filter(dd => dd.document_type?.includes('relieving')).length > idx)
        );
        const hasPayslip = allDocs.some(d => 
          (d.document_type?.toLowerCase().includes('payslip') || d.document_type?.toLowerCase().includes('salary_slip') || d.file_name?.toLowerCase().includes('payslip'))
        );
        const hasOfferLetter = allDocs.some(d => 
          (d.document_type?.toLowerCase().includes('offer') || d.file_name?.toLowerCase().includes('offer'))
        );
        
        if (!hasRelievingLetter && !emp.is_current) {
          issues.push({ section: `Employment #${idx + 1}`, field: 'Relieving Letter', status: 'missing', docType: 'relieving_letter' });
        }
        if (!hasPayslip) {
          issues.push({ section: `Employment #${idx + 1}`, field: 'Payslip/Salary Slip', status: 'missing', docType: 'payslip' });
        }
      });
    }
    
    // Education documents
    const hasEducationCheck = checks.some(c => c.check_type === 'education_check');
    if (hasEducationCheck && employee?.education_entries?.length > 0) {
      employee.education_entries.forEach((edu, idx) => {
        const hasCertificate = allDocs.some(d => 
          d.document_type?.toLowerCase().includes('degree') || 
          d.document_type?.toLowerCase().includes('certificate') ||
          d.document_type?.toLowerCase().includes('marksheet') ||
          d.file_name?.toLowerCase().includes('degree') ||
          d.file_name?.toLowerCase().includes('marksheet')
        );
        
        if (!hasCertificate) {
          issues.push({ section: `Education #${idx + 1}`, field: 'Degree/Marksheet', status: 'missing', docType: 'education_certificate' });
        }
      });
    }
    
    return issues;
  };

  // Get combined insufficiencies (entry + auto-detected + documents)
  const getCombinedInsufficiencies = () => {
    const autoDetected = detectMissingData(viewEmployee);
    
    // Get all documents for this case from checkDocuments state
    const allCaseDocs = Object.values(checkDocuments).flat();
    const docIssues = detectMissingDocuments(viewCase, viewEmployee, allCaseDocs);
    
    // Map entry insufficiencies to display format
    const entryMapped = entryInsufficiencies.map(i => ({
      id: `${i.entry_type}_${i.entry_index}_${i.field_name}`,
      section: i.entry_type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
      field: i.field_name?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
      reason: i.reason,
      status: i.status || 'pending',
      entry_type: i.entry_type,
      entry_index: i.entry_index,
      field_name: i.field_name,
      isEntryInsuff: true
    }));
    
    // Map auto-detected to display format
    const autoMapped = autoDetected.map(i => ({
      id: `auto_${i.section}_${i.field}`,
      section: i.section,
      field: i.field,
      reason: 'Auto-detected missing data',
      status: 'pending',
      isAutoDetected: true
    }));
    
    // Map document issues to display format
    const docMapped = docIssues.map(i => ({
      id: `doc_${i.section}_${i.field}`,
      section: i.section,
      field: i.field,
      reason: 'Missing required document',
      status: 'pending',
      isDocumentIssue: true,
      docType: i.docType
    }));
    
    // Combine and dedupe
    const combined = [...entryMapped];
    autoMapped.forEach(auto => {
      const exists = combined.some(e => 
        e.section.toLowerCase() === auto.section.toLowerCase() && 
        e.field.toLowerCase() === auto.field.toLowerCase()
      );
      if (!exists) combined.push(auto);
    });
    
    // Add document issues
    docMapped.forEach(doc => {
      const exists = combined.some(e => 
        e.section.toLowerCase() === doc.section.toLowerCase() && 
        e.field.toLowerCase() === doc.field.toLowerCase()
      );
      if (!exists) combined.push(doc);
    });
    
    return combined;
  };

  const getCheckStatusBadge = (status, hasInsuff, insuffStatus) => {
    if (insuffStatus === 'waiting') return 'bg-amber-100 text-amber-700';
    if (hasInsuff) return 'bg-rose-100 text-rose-700';
    const map = {
      'pending': 'bg-slate-100 text-slate-700',
      'in_progress': 'bg-blue-100 text-blue-700',
      'verified': 'bg-emerald-100 text-emerald-700',
      'failed': 'bg-rose-100 text-rose-700'
    };
    return map[status] || map['pending'];
  };

  if (loading) return <div className="p-8 text-center">Loading...</div>;

  if (selectedCheck) {
    return (
      <CheckDetailsView
        caseId={selectedCheck.caseId}
        checkId={selectedCheck.checkId}
        onBack={() => { setSelectedCheck(null); loadData(); }}
        onUpdate={loadData}
      />
    );
  }

  // Always show the cases list view - users click "Review" to open case details


  return (
    <div>
      <div className="mb-8 flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2">QC Review</h1>
          <p className="text-slate-600">Review candidate data and documents, approve or raise insufficiencies</p>
        </div>
        {cases.length > 0 && (
          <div className="flex items-center gap-3">
            <Button 
              variant="outline" 
              size="sm"
              onClick={toggleAllCases}
              data-testid="qc-select-all-cases"
            >
              {selectedCaseIds.size === cases.length ? 'Deselect All' : 'Select All'}
            </Button>
            {selectedCaseIds.size > 0 && (
              <Button 
                size="sm" 
                className="bg-blue-600 hover:bg-blue-700 text-white"
                onClick={() => setShowBulkSubmitDialog(true)}
                data-testid="qc-bulk-submit-btn"
              >
                <ClipboardCheck className="w-4 h-4 mr-1" />
                Bulk Submit ({selectedCaseIds.size})
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Bulk Submit Confirmation Dialog */}
      <Dialog open={showBulkSubmitDialog} onOpenChange={setShowBulkSubmitDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Bulk Submit Cases</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-slate-600 mb-2">
              You are about to submit <span className="font-bold text-slate-900">{selectedCaseIds.size}</span> draft case(s) for QC review.
            </p>
            <p className="text-sm text-slate-500">
              This will move the selected draft cases to <strong>Pending QC</strong> status for formal review and approval.
            </p>
            <div className="mt-4 max-h-40 overflow-y-auto border rounded p-2">
              {Array.from(selectedCaseIds).map(id => {
                const c = cases.find(x => x.id === id);
                return (
                  <div key={id} className="text-sm py-1 border-b last:border-0">
                    <span className="font-mono text-slate-500">{id}</span>
                    <span className="ml-2 text-slate-700">{c?.employee_name || getEmployeeName(c?.employee_id)}</span>
                    <span className="ml-2 text-xs text-slate-400">{c?.checks?.length || 0} checks</span>
                  </div>
                );
              })}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBulkSubmitDialog(false)} data-testid="bulk-submit-cancel">Cancel</Button>
            <Button 
              className="bg-blue-600 hover:bg-blue-700" 
              onClick={handleBulkSubmitCases}
              disabled={bulkSubmitting}
              data-testid="bulk-submit-confirm"
            >
              {bulkSubmitting ? 'Submitting...' : `Submit ${selectedCaseIds.size} Cases`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="space-y-4">
        {cases.map((caseItem) => {
          const checksWithoutInsuff = caseItem.checks?.filter(c => !hasOpenInsuff(caseItem.id, c.check_id)).length || 0;
          
          return (
            <Card key={caseItem.id} className={`border-slate-200 shadow-sm ${selectedCaseIds.has(caseItem.id) ? 'ring-2 ring-blue-400 border-blue-300' : ''}`} data-testid={`qc-case-card-${caseItem.id}`}>
              <CardHeader className="pb-2">
                <div className="flex justify-between items-start">
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      checked={selectedCaseIds.has(caseItem.id)}
                      onChange={() => toggleCaseSelection(caseItem.id)}
                      className="mt-1.5 h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                      data-testid={`qc-case-checkbox-${caseItem.id}`}
                    />
                    <div>
                      <CardTitle className="text-lg">{getEmployeeName(caseItem.employee_id)}</CardTitle>
                      <p className="text-sm text-slate-500 font-mono">{caseItem.id}</p>
                      <div className="flex gap-4 mt-1 text-xs text-slate-500">
                        <span>Request Raised: {(caseItem.invitation_sent_at || caseItem.created_at) ? new Date(caseItem.invitation_sent_at || caseItem.created_at).toLocaleDateString() : '-'}</span>
                        <span>Submitted: {caseItem.submitted_at
                          ? new Date(caseItem.submitted_at).toLocaleDateString()
                          : ((caseItem.employee_submitted || caseItem.source === 'bulk_import' || caseItem.source === 'data_entry')
                              ? (caseItem.created_at ? new Date(caseItem.created_at).toLocaleDateString() : '-')
                              : 'Pending')}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className="bg-amber-100 text-amber-700">Pending QC</Badge>
                    <Button variant="outline" size="sm" onClick={() => openViewCase(caseItem)} data-testid={`view-case-${caseItem.id}`}>
                      <Eye className="w-4 h-4 mr-1" />View All
                    </Button>
                    {checksWithoutInsuff > 0 && (
                      <Button 
                        size="sm" 
                        className="bg-emerald-600 hover:bg-emerald-700"
                        onClick={() => handleAutoApproveAll(caseItem)}
                      >
                        <CheckCircle className="w-4 h-4 mr-1" />
                        Auto-Approve ({checksWithoutInsuff})
                      </Button>
                    )}
                  </div>
                </div>
                
                {/* Bulk Selection Actions */}
                <div className="flex items-center gap-2 mt-3 pt-3 border-t border-slate-100">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => selectAllChecks(caseItem)}
                    className="text-xs"
                  >
                    Select All Eligible
                  </Button>
                  {getSelectedCount(caseItem.id) > 0 && (
                    <>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => clearAllSelections(caseItem.id)}
                        className="text-xs text-slate-500"
                      >
                        Clear Selection
                      </Button>
                      <div className="flex-1" />
                      <Badge className="bg-blue-100 text-blue-700">
                        {getSelectedCount(caseItem.id)} selected
                      </Badge>
                      <Button 
                        size="sm" 
                        className="bg-emerald-600 hover:bg-emerald-700"
                        onClick={() => setShowBulkUpdateDialog(caseItem.id)}
                        data-testid={`bulk-verify-${caseItem.id}`}
                      >
                        <ClipboardCheck className="w-4 h-4 mr-1" />
                        Bulk Update Status
                      </Button>
                    </>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10">
                        <span className="sr-only">Select</span>
                      </TableHead>
                      <TableHead>Check ID</TableHead>
                      <TableHead>Check Type</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Insufficiency</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {caseItem.checks?.map((check, idx) => {
                      const insuffStatus = getInsuffStatus(caseItem.id, check.check_id);
                      const hasInsuff = hasOpenInsuff(caseItem.id, check.check_id);
                      const checkType = typeof check.check_type === 'object' ? check.check_type.check_type : check.check_type;
                      const canSelect = insuffStatus !== 'open' && insuffStatus !== 'waiting';
                      
                      return (
                        <TableRow key={idx} className={isCheckSelected(caseItem.id, checkType) ? 'bg-blue-50' : ''}>
                          <TableCell>
                            <input
                              type="checkbox"
                              checked={isCheckSelected(caseItem.id, checkType)}
                              onChange={() => toggleCheckSelection(caseItem.id, checkType)}
                              disabled={!canSelect}
                              className="w-4 h-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500 disabled:opacity-50"
                              data-testid={`select-check-${caseItem.id}-${checkType}`}
                            />
                          </TableCell>
                          <TableCell className="font-mono text-sm">{check.check_id}</TableCell>
                          <TableCell>{CHECK_NAMES[checkType] || checkType}</TableCell>
                          <TableCell>
                            <Badge className={getCheckStatusBadge(check.status, check.has_insufficiency, insuffStatus)}>
                              {check.status?.toUpperCase()}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {insuffStatus === 'waiting' ? (
                              <Badge className="bg-amber-100 text-amber-700">
                                <Clock className="w-3 h-3 mr-1" />Waiting Clear
                              </Badge>
                            ) : insuffStatus === 'open' ? (
                              <Badge className="bg-rose-100 text-rose-700">
                                <AlertTriangle className="w-3 h-3 mr-1" />Open
                              </Badge>
                            ) : check.has_insufficiency ? (
                              <Badge className="bg-slate-100 text-slate-600">Cleared</Badge>
                            ) : (
                              <span className="text-emerald-600">✓ Clear</span>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              <Button variant="ghost" size="sm" onClick={() => openCheckDetails(caseItem, check)}>
                                <Eye className="w-4 h-4" />
                              </Button>
                              {!hasInsuff ? (
                                <>
                                  <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    className="text-rose-600"
                                    onClick={() => setShowInsuffDialog({ caseId: caseItem.id, checkId: check.check_id })}
                                  >
                                    <AlertTriangle className="w-4 h-4" />
                                  </Button>
                                  <Button 
                                    size="sm" 
                                    className="bg-emerald-600 hover:bg-emerald-700"
                                    onClick={() => setShowApproveDialog({ caseId: caseItem.id, checkId: check.check_id, hasInsuff: false })}
                                  >
                                    <UserPlus className="w-4 h-4 mr-1" />Assign
                                  </Button>
                                </>
                              ) : insuffStatus === 'waiting' ? (
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  className="border-emerald-300 text-emerald-600"
                                  onClick={() => handleClearInsufficiency(caseItem.id, check.check_id)}
                                >
                                  <CheckCircle className="w-4 h-4 mr-1" />Clear
                                </Button>
                              ) : (
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  className="border-amber-300 text-amber-600"
                                  onClick={() => setShowApproveDialog({ caseId: caseItem.id, checkId: check.check_id, hasInsuff: true })}
                                >
                                  Bypass & Assign
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          );
        })}

        {cases.length === 0 && (
          <Card className="border-slate-200">
            <CardContent className="text-center py-12">
              <FileCheck className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-600">No cases pending QC review</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Case Review Dialog - Enhanced with Tabs */}
      <Dialog open={!!viewCase} onOpenChange={(o) => { if (!o) { setViewCase(null); setViewEmployee(null); setCheckDocuments({}); } }}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col [&>button.absolute]:z-50">
          <DialogHeader className="border-b pb-4 pr-8">
            <div className="flex justify-between items-start">
              <div>
                <DialogTitle className="text-xl">Case Review</DialogTitle>
                <p className="text-sm text-slate-500 font-mono mt-1">{viewCase?.id}</p>
              </div>
              <div className="text-right mr-4">
                <div className="flex items-center justify-end gap-2">
                  <p className="font-medium">{viewEmployee?.name || 'N/A'}</p>
                  <Badge variant="outline" className={
                    viewEmployee?.employee_category === 'fresher' ? 'bg-green-50 text-green-700 border-green-300' :
                    viewEmployee?.employee_category === 'intern' ? 'bg-purple-50 text-purple-700 border-purple-300' :
                    viewEmployee?.employee_category === 'trainee' ? 'bg-orange-50 text-orange-700 border-orange-300' :
                    'bg-blue-50 text-blue-700 border-blue-300'
                  }>
                    {(viewEmployee?.employee_category || (viewEmployee?.is_fresher ? 'fresher' : 'experienced')).charAt(0).toUpperCase() + (viewEmployee?.employee_category || (viewEmployee?.is_fresher ? 'fresher' : 'experienced')).slice(1)}
                  </Badge>
                </div>
                <p className="text-xs text-slate-500">{viewCase?.client_name} • {viewCase?.entity_name}</p>
                <Badge className="bg-amber-100 text-amber-700 mt-1">Pending QC</Badge>
              </div>
            </div>
          </DialogHeader>
          
          {/* Warning banner for pending client approval */}
          {viewCase?.status === 'pending_client_approval' && (
            <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 mx-4 mt-2 flex items-center gap-3">
              <Clock className="w-6 h-6 text-indigo-600 flex-shrink-0" />
              <div>
                <p className="font-semibold text-indigo-800">Pending Client Approval</p>
                <p className="text-sm text-indigo-600">This case requires client approval before QC can process. Please wait for the client to approve.</p>
              </div>
            </div>
          )}
          
          <Tabs value={activeReviewTab} onValueChange={setActiveReviewTab} className="flex-1 overflow-hidden flex flex-col">
            <TabsList className="w-full justify-start rounded-none border-b bg-slate-50 px-2 overflow-x-auto" style={{display: 'flex', flexWrap: 'nowrap', overflowX: 'auto', flexShrink: 0}}>
              <TabsTrigger value="personal" className="rounded-none data-[state=active]:border-b-2 data-[state=active]:border-purple-600 whitespace-nowrap text-xs px-2">Personal</TabsTrigger>
              <TabsTrigger value="address" className="rounded-none data-[state=active]:border-b-2 data-[state=active]:border-purple-600 whitespace-nowrap text-xs px-2">Address</TabsTrigger>
              <TabsTrigger value="employment" style={{display: viewCase?.checks?.some(c => (typeof c.check_type === 'object' ? c.check_type.check_type : c.check_type) === 'employment_check') ? undefined : 'none'}} className="rounded-none data-[state=active]:border-b-2 data-[state=active]:border-purple-600 whitespace-nowrap text-xs px-2">Employment</TabsTrigger>
              <TabsTrigger value="education" className="rounded-none data-[state=active]:border-b-2 data-[state=active]:border-purple-600 whitespace-nowrap text-xs px-2">Education</TabsTrigger>
              <TabsTrigger value="criminal_court_check" style={{display: viewCase?.checks?.some(c => {const t = typeof c.check_type === 'object' ? c.check_type.check_type : c.check_type; return t === 'criminal_court_check' || t === 'court_advocate_check';}) ? undefined : 'none'}} className="rounded-none data-[state=active]:border-b-2 data-[state=active]:border-purple-600 whitespace-nowrap text-xs px-2">Criminal Court</TabsTrigger>
              <TabsTrigger value="global_database" style={{display: viewCase?.checks?.some(c => (typeof c.check_type === 'object' ? c.check_type.check_type : c.check_type) === 'global_database') ? undefined : 'none'}} className="rounded-none data-[state=active]:border-b-2 data-[state=active]:border-purple-600 whitespace-nowrap text-xs px-2">Global Database</TabsTrigger>
              <TabsTrigger value="india_criminal_db" style={{display: viewCase?.checks?.some(c => (typeof c.check_type === 'object' ? c.check_type.check_type : c.check_type) === 'india_criminal_db') ? undefined : 'none'}} className="rounded-none data-[state=active]:border-b-2 data-[state=active]:border-purple-600 whitespace-nowrap text-xs px-2">India Criminal DB</TabsTrigger>
              <TabsTrigger value="world_criminal_db" style={{display: viewCase?.checks?.some(c => (typeof c.check_type === 'object' ? c.check_type.check_type : c.check_type) === 'world_criminal_db') ? undefined : 'none'}} className="rounded-none data-[state=active]:border-b-2 data-[state=active]:border-purple-600 whitespace-nowrap text-xs px-2">World Criminal DB</TabsTrigger>
              <TabsTrigger value="credit_check" style={{display: viewCase?.checks?.some(c => (typeof c.check_type === 'object' ? c.check_type.check_type : c.check_type) === 'credit_check') ? undefined : 'none'}} className="rounded-none data-[state=active]:border-b-2 data-[state=active]:border-purple-600 whitespace-nowrap text-xs px-2">Credit</TabsTrigger>
              <TabsTrigger value="drug_test" style={{display: viewCase?.checks?.some(c => (typeof c.check_type === 'object' ? c.check_type.check_type : c.check_type) === 'drug_test') ? undefined : 'none'}} className="rounded-none data-[state=active]:border-b-2 data-[state=active]:border-purple-600 whitespace-nowrap text-xs px-2">Drug Test</TabsTrigger>
              <TabsTrigger value="reference_check" style={{display: viewCase?.checks?.some(c => {const t = typeof c.check_type === 'object' ? c.check_type.check_type : c.check_type; return t === 'reference_check' || t === 'personal_reference' || t === 'professional_reference';}) ? undefined : 'none'}} className="rounded-none data-[state=active]:border-b-2 data-[state=active]:border-purple-600 whitespace-nowrap text-xs px-2">Reference</TabsTrigger>
              <TabsTrigger value="documents" className="rounded-none data-[state=active]:border-b-2 data-[state=active]:border-purple-600 whitespace-nowrap text-xs px-2">Docs</TabsTrigger>
              <TabsTrigger value="insufficiencies" className="rounded-none data-[state=active]:border-b-2 data-[state=active]:border-rose-600 whitespace-nowrap text-xs px-2">Insuff</TabsTrigger>
              <TabsTrigger value="checks" className="rounded-none data-[state=active]:border-b-2 data-[state=active]:border-purple-600 whitespace-nowrap text-xs px-2">Checks ({viewCase?.checks?.length || 0})</TabsTrigger>
            </TabsList>

            <div className="flex-1 overflow-y-auto p-4">
              {/* Personal Tab */}
              <TabsContent value="personal" className="m-0">
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-slate-50 p-3 rounded-lg">
                    <p className="text-xs text-slate-500 uppercase">Full Name</p>
                    <p className="font-medium">{viewEmployee?.name || '-'}</p>
                  </div>
                  <div className="bg-slate-50 p-3 rounded-lg">
                    <p className="text-xs text-slate-500 uppercase">Father&apos;s Name</p>
                    <p className="font-medium">{viewEmployee?.father_name || '-'}</p>
                  </div>
                  <div className="bg-slate-50 p-3 rounded-lg">
                    <p className="text-xs text-slate-500 uppercase">Date of Birth</p>
                    <p className="font-medium">{viewEmployee?.dob || '-'}</p>
                  </div>
                  <div className="bg-slate-50 p-3 rounded-lg">
                    <p className="text-xs text-slate-500 uppercase">Gender</p>
                    <p className="font-medium">{viewEmployee?.gender || '-'}</p>
                  </div>
                  <div className="bg-slate-50 p-3 rounded-lg">
                    <p className="text-xs text-slate-500 uppercase">Email</p>
                    <p className="font-medium">{viewEmployee?.email || '-'}</p>
                  </div>
                  <div className="bg-slate-50 p-3 rounded-lg">
                    <p className="text-xs text-slate-500 uppercase">Phone</p>
                    <p className="font-medium">{viewEmployee?.phone || '-'}</p>
                  </div>
                  <div className="bg-slate-50 p-3 rounded-lg">
                    <p className="text-xs text-slate-500 uppercase">Alternate Contact</p>
                    <p className="font-medium">{viewEmployee?.alternate_phone || '-'}</p>
                  </div>
                  <div className="bg-slate-50 p-3 rounded-lg">
                    <p className="text-xs text-slate-500 uppercase">Employee ID</p>
                    <p className="font-medium">{viewEmployee?.employee_id || '-'}</p>
                  </div>
                  <div className="bg-slate-50 p-3 rounded-lg">
                    <p className="text-xs text-slate-500 uppercase">PAN Number</p>
                    <p className="font-medium font-mono">{viewEmployee?.pan_number || '-'}</p>
                  </div>
                  <div className="bg-slate-50 p-3 rounded-lg">
                    <p className="text-xs text-slate-500 uppercase">Aadhar Number</p>
                    <p className="font-medium font-mono">{viewEmployee?.aadhar_number || '-'}</p>
                  </div>
                </div>
              </TabsContent>

              {/* Address Tab */}
              <TabsContent value="address" className="m-0 space-y-4">
                <div>
                  <h4 className="font-semibold mb-2 text-slate-800">Present Address</h4>
                  <div className="bg-slate-50 p-4 rounded-lg">
                    {(() => {
                      const addr = formatAddress(viewEmployee, 'present');
                      if (!addr) return <p className="text-slate-500">-</p>;
                      if (viewEmployee?.present_address?.complete_address) {
                        return (
                          <>
                            <p>{viewEmployee.present_address.complete_address}</p>
                            {viewEmployee.present_address.landmark && <p className="text-sm text-slate-600">Landmark: {viewEmployee.present_address.landmark}</p>}
                            <p className="text-sm text-slate-600">{[viewEmployee.present_address.city, viewEmployee.present_address.state, viewEmployee.present_address.pincode].filter(Boolean).join(', ')}</p>
                          </>
                        );
                      }
                      return (
                        <>
                          <p>{[viewEmployee?.present_house_no, viewEmployee?.present_cross, viewEmployee?.present_main, viewEmployee?.present_area].filter(Boolean).join(', ') || '-'}</p>
                          <p className="text-sm text-slate-600">{[viewEmployee?.present_city, viewEmployee?.present_state, viewEmployee?.present_pincode].filter(Boolean).join(', ')}</p>
                        </>
                      );
                    })()}
                  </div>
                </div>
                <div>
                  <h4 className="font-semibold mb-2 text-slate-800">Permanent Address</h4>
                  <div className="bg-slate-50 p-4 rounded-lg">
                    {(() => {
                      const addr = formatAddress(viewEmployee, 'permanent');
                      if (!addr) return <p className="text-slate-500">-</p>;
                      if (viewEmployee?.permanent_address?.complete_address) {
                        return (
                          <>
                            <p>{viewEmployee.permanent_address.complete_address}</p>
                            {viewEmployee.permanent_address.landmark && <p className="text-sm text-slate-600">Landmark: {viewEmployee.permanent_address.landmark}</p>}
                            <p className="text-sm text-slate-600">{[viewEmployee.permanent_address.city, viewEmployee.permanent_address.state, viewEmployee.permanent_address.pincode].filter(Boolean).join(', ')}</p>
                          </>
                        );
                      }
                      return (
                        <>
                          <p>{[viewEmployee?.permanent_house_no, viewEmployee?.permanent_cross, viewEmployee?.permanent_main, viewEmployee?.permanent_area].filter(Boolean).join(', ') || '-'}</p>
                          <p className="text-sm text-slate-600">{[viewEmployee?.permanent_city, viewEmployee?.permanent_state, viewEmployee?.permanent_pincode].filter(Boolean).join(', ')}</p>
                        </>
                      );
                    })()}
                  </div>
                </div>
                {viewEmployee?.additional_addresses?.length > 0 && (
                  <div>
                    <h4 className="font-semibold mb-2 text-slate-800">Additional Addresses</h4>
                    {viewEmployee.additional_addresses.map((addr, idx) => (
                      <div key={idx} className="bg-slate-50 p-4 rounded-lg mb-2">
                        <Badge className="mb-2 bg-purple-100 text-purple-700">{addr.address_type || 'Additional'}</Badge>
                        <p>{addr.complete_address || [addr.house_no, addr.cross, addr.main, addr.area].filter(Boolean).join(', ') || '-'}</p>
                        <p className="text-sm text-slate-600">{[addr.city, addr.state, addr.pincode].filter(Boolean).join(', ')}</p>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>

              {/* Employment Tab */}
              <TabsContent value="employment" className="m-0">
                {viewEmployee?.employment_entries?.length > 0 ? (
                  <div className="space-y-4">
                    {viewEmployee.employment_entries.map((emp, idx) => (
                      <div key={idx} className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                        <div className="flex justify-between items-start mb-2">
                          <h4 className="font-semibold text-slate-800">{emp.company_name || 'Company'}</h4>
                          <div className="flex gap-2">
                            {emp.is_current && <Badge className="bg-emerald-100 text-emerald-700 text-xs">Current</Badge>}
                            <Badge variant="outline">{emp.employment_type || 'Employment'}</Badge>
                          </div>
                        </div>
                        <div className="grid grid-cols-3 gap-3 text-sm">
                          <div><span className="text-slate-500">Designation:</span> {emp.designation || '-'}</div>
                          <div><span className="text-slate-500">Employee ID:</span> {emp.employee_id || '-'}</div>
                          <div><span className="text-slate-500">CTC/Salary:</span> {emp.ctc || '-'}</div>
                          <div><span className="text-slate-500">Joining:</span> {formatDateDDMonYYYY(emp.start_date) || emp.start_date || '-'}</div>
                          <div><span className="text-slate-500">Relieving:</span> {emp.end_date ? (formatDateDDMonYYYY(emp.end_date) || emp.end_date) : 'Present'}</div>
                          <div><span className="text-slate-500">Location:</span> {emp.work_location || '-'}</div>
                          {emp.uan_pf_number && <div><span className="text-slate-500">UAN/PF:</span> {emp.uan_pf_number}</div>}
                          {emp.reimbursements && <div><span className="text-slate-500">Reimbursements:</span> {emp.reimbursements}</div>}
                        </div>
                        {(emp.hr_name || emp.hr_email || emp.hr_phone) && (
                          <div className="mt-3 pt-3 border-t border-slate-200">
                            <p className="text-xs text-slate-500 uppercase mb-1">HR Contact</p>
                            <div className="grid grid-cols-3 gap-3 text-sm">
                              <div><span className="text-slate-500">Name:</span> {emp.hr_name || '-'}</div>
                              <div><span className="text-slate-500">Email:</span> {emp.hr_email || '-'}</div>
                              <div><span className="text-slate-500">Phone:</span> {emp.hr_phone || '-'}</div>
                            </div>
                          </div>
                        )}
                        {(emp.reporting_manager_name || emp.reporting_manager_email || emp.reporting_manager_phone) && (
                          <div className="mt-3 pt-3 border-t border-slate-200">
                            <p className="text-xs text-slate-500 uppercase mb-1">Reporting Manager</p>
                            <div className="grid grid-cols-3 gap-3 text-sm">
                              <div><span className="text-slate-500">Name:</span> {emp.reporting_manager_name || '-'}</div>
                              <div><span className="text-slate-500">Email:</span> {emp.reporting_manager_email || '-'}</div>
                              <div><span className="text-slate-500">Phone:</span> {emp.reporting_manager_phone || '-'}</div>
                              {emp.reporting_manager_designation && <div><span className="text-slate-500">Designation:</span> {emp.reporting_manager_designation}</div>}
                              {emp.reporting_manager_department && <div><span className="text-slate-500">Department:</span> {emp.reporting_manager_department}</div>}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <Briefcase className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                    <p>No employment records available</p>
                  </div>
                )}
              </TabsContent>

              {/* Education Tab */}
              <TabsContent value="education" className="m-0">
                {viewEmployee?.education_entries?.length > 0 ? (
                  <div className="space-y-4">
                    {viewEmployee.education_entries.map((edu, idx) => (
                      <div key={idx} className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                        <div className="flex justify-between items-start mb-2">
                          <h4 className="font-semibold text-slate-800">{edu.institution_name || 'Institution'}</h4>
                          <Badge variant="outline">{edu.qualification || edu.course || 'Education'}</Badge>
                        </div>
                        <div className="grid grid-cols-3 gap-3 text-sm">
                          <div><span className="text-slate-500">Course:</span> {edu.course || '-'}</div>
                          <div><span className="text-slate-500">University:</span> {edu.university || '-'}</div>
                          <div><span className="text-slate-500">Year:</span> {edu.year_of_passing || '-'}</div>
                          <div><span className="text-slate-500">Roll No:</span> {edu.roll_number || '-'}</div>
                          <div><span className="text-slate-500">Grade/CGPA:</span> {edu.grade || '-'}</div>
                          {edu.specialization && <div><span className="text-slate-500">Specialization:</span> {edu.specialization}</div>}
                          {edu.name_as_per_doc && <div><span className="text-slate-500">Name on Doc:</span> {edu.name_as_per_doc}</div>}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <GraduationCap className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                    <p>No education records available</p>
                  </div>
                )}
              </TabsContent>

              {/* Documents Tab */}
              <TabsContent value="documents" className="m-0">
                {loadingDocuments ? (
                  <div className="text-center py-8">
                    <div className="animate-spin w-8 h-8 border-2 border-purple-600 border-t-transparent rounded-full mx-auto mb-2"></div>
                    <p className="text-slate-500">Loading documents...</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Declaration Form - shown at top if available */}
                    {(() => {
                      const empDocs = checkDocuments['employee_docs'] || [];
                      const declDoc = empDocs.find(d => d.document_type === 'declaration_form');
                      const declUrl = viewEmployee?.declaration_form_url;
                      const hasDeclDoc = declDoc || declUrl;
                      if (!hasDeclDoc) return null;
                      const doc = declDoc || {
                        document_type: 'declaration_form',
                        original_filename: 'Candidate Declaration Form',
                        file_url: declUrl,
                        file_type: 'application/pdf'
                      };
                      return (
                        <div className="border border-indigo-200 rounded-lg bg-indigo-50/30">
                          <div className="bg-indigo-50 px-4 py-2 border-b border-indigo-200 flex justify-between items-center">
                            <span className="font-medium text-indigo-800">Candidate Declaration Form</span>
                            <Badge className="bg-indigo-100 text-indigo-700">Signed</Badge>
                          </div>
                          <div className="p-4">
                            <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-indigo-100">
                              <div className="flex items-center gap-2">
                                <FileText className="w-5 h-5 text-indigo-500" />
                                <div>
                                  <p className="text-sm font-medium">{doc.original_filename || 'Declaration Form'}</p>
                                  <p className="text-xs text-slate-500">declaration_form</p>
                                </div>
                              </div>
                              <div className="flex gap-1">
                                <Button variant="ghost" size="sm" onClick={() => setPreviewDocument(doc)}>
                                  <Eye className="w-4 h-4" />
                                </Button>
                                {doc.file_url && (
                                  <Button variant="ghost" size="sm" onClick={() => window.open(doc.file_url, '_blank')}>
                                    <ExternalLink className="w-4 h-4" />
                                  </Button>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                    {viewCase?.checks?.map((check, idx) => {
                      const docs = checkDocuments[check.check_id] || [];
                      return (
                        <div key={idx} className="border border-slate-200 rounded-lg">
                          <div className="bg-slate-50 px-4 py-2 border-b border-slate-200 flex justify-between items-center">
                            <div>
                              <span className="font-medium">{CHECK_NAMES[check.check_type] || check.check_type}</span>
                              <span className="text-xs text-slate-500 ml-2">#{check.check_id}</span>
                            </div>
                            <Badge className={docs.length > 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}>
                              {docs.length} document{docs.length !== 1 ? 's' : ''}
                            </Badge>
                          </div>
                          <div className="p-4">
                            {docs.length > 0 ? (
                              <div className="grid grid-cols-2 gap-3">
                                {docs.map((doc, dIdx) => (
                                  <div key={dIdx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-100">
                                    <div className="flex items-center gap-2">
                                      {doc.file_type?.includes('image') ? (
                                        <Image className="w-5 h-5 text-blue-500" />
                                      ) : (
                                        <FileText className="w-5 h-5 text-slate-500" />
                                      )}
                                      <div>
                                        <p className="text-sm font-medium truncate max-w-[180px]">{doc.original_filename || doc.document_type || 'Document'}</p>
                                        <p className="text-xs text-slate-500">{doc.document_type}</p>
                                      </div>
                                    </div>
                                    <div className="flex gap-1">
                                      <Button 
                                        variant="ghost" 
                                        size="sm"
                                        onClick={() => setPreviewDocument(doc)}
                                      >
                                        <Eye className="w-4 h-4" />
                                      </Button>
                                      {doc.file_url && (
                                        <Button 
                                          variant="ghost" 
                                          size="sm"
                                          onClick={() => window.open(doc.file_url, '_blank')}
                                        >
                                          <ExternalLink className="w-4 h-4" />
                                        </Button>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-center text-slate-500 py-4">No documents uploaded for this check</p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </TabsContent>

              {/* Insufficiencies Tab - Shows missing data with RED/GREEN status */}
              <TabsContent value="insufficiencies" className="m-0">
                {(() => {
                  const allInsuffs = getCombinedInsufficiencies();
                  const pendingCount = allInsuffs.filter(i => i.status !== 'cleared').length;
                  const clearedCount = allInsuffs.filter(i => i.status === 'cleared').length;
                  const docCount = allInsuffs.filter(i => i.isDocumentIssue && i.status !== 'cleared').length;
                  const dataCount = pendingCount - docCount;
                  
                  return (
                    <div className="space-y-4">
                      {/* Summary Cards */}
                      <div className="grid grid-cols-3 gap-4 mb-4">
                        <div className="bg-rose-50 border border-rose-200 rounded-lg p-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-rose-100 rounded-full flex items-center justify-center">
                              <AlertTriangle className="w-5 h-5 text-rose-600" />
                            </div>
                            <div>
                              <p className="text-2xl font-bold text-rose-700">{dataCount}</p>
                              <p className="text-sm text-rose-600">Missing Data</p>
                            </div>
                          </div>
                        </div>
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                              <FileText className="w-5 h-5 text-amber-600" />
                            </div>
                            <div>
                              <p className="text-2xl font-bold text-amber-700">{docCount}</p>
                              <p className="text-sm text-amber-600">Missing Documents</p>
                            </div>
                          </div>
                        </div>
                        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
                              <CheckCircle className="w-5 h-5 text-emerald-600" />
                            </div>
                            <div>
                              <p className="text-2xl font-bold text-emerald-700">{clearedCount}</p>
                              <p className="text-sm text-emerald-600">Cleared</p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Insufficiency Items */}
                      {allInsuffs.length > 0 ? (
                        <div className="space-y-2">
                          {allInsuffs.map((insuff, idx) => (
                            <div 
                              key={insuff.id || idx}
                              className={`p-4 rounded-lg border-l-4 ${
                                insuff.status === 'cleared'
                                  ? 'bg-emerald-50 border-emerald-500'
                                  : insuff.isDocumentIssue
                                    ? 'bg-amber-50 border-amber-500'
                                    : 'bg-rose-50 border-rose-500'
                              }`}
                              data-testid={`insufficiency-item-${idx}`}
                            >
                              <div className="flex items-start justify-between">
                                <div className="flex items-start gap-3">
                                  {insuff.status === 'cleared' ? (
                                    <CheckCircle className="w-5 h-5 text-emerald-600 mt-0.5" />
                                  ) : insuff.isDocumentIssue ? (
                                    <FileText className="w-5 h-5 text-amber-600 mt-0.5" />
                                  ) : (
                                    <AlertTriangle className="w-5 h-5 text-rose-600 mt-0.5" />
                                  )}
                                  <div>
                                    <div className="flex items-center gap-2 mb-1">
                                      <span className="font-semibold text-slate-800">{insuff.section}</span>
                                      <span className="text-slate-400">•</span>
                                      <span className="text-slate-600">{insuff.field}</span>
                                    </div>
                                    <p className="text-sm text-slate-500">{insuff.reason}</p>
                                    <div className="flex gap-2 mt-1">
                                      {insuff.isAutoDetected && (
                                        <Badge className="bg-slate-100 text-slate-600 text-xs">Auto-detected</Badge>
                                      )}
                                      {insuff.isDocumentIssue && (
                                        <Badge className="bg-amber-100 text-amber-700 text-xs">
                                          <FileText className="w-3 h-3 mr-1" />Document
                                        </Badge>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  {insuff.status === 'cleared' ? (
                                    <Badge className="bg-emerald-100 text-emerald-700">
                                      <CheckCircle className="w-3 h-3 mr-1" />Cleared
                                    </Badge>
                                  ) : (
                                    <>
                                      <Badge className="bg-rose-100 text-rose-700">
                                        <AlertTriangle className="w-3 h-3 mr-1" />Action Pending
                                      </Badge>
                                      {insuff.isEntryInsuff && (
                                        <Button 
                                          size="sm" 
                                          variant="outline"
                                          className="border-emerald-300 text-emerald-600 hover:bg-emerald-50"
                                          onClick={() => handleClearEntryInsufficiency(insuff.entry_type, insuff.entry_index, insuff.field_name)}
                                        >
                                          <CheckCircle className="w-3 h-3 mr-1" />Clear
                                        </Button>
                                      )}
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-12 bg-emerald-50 rounded-lg">
                          <CheckCircle className="w-16 h-16 text-emerald-400 mx-auto mb-4" />
                          <p className="text-emerald-700 font-medium text-lg">All Data Complete</p>
                          <p className="text-emerald-600 text-sm">No insufficiencies found</p>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </TabsContent>

              {/* Dynamic check type tabs content */}
              {(() => {
                const standardTypes = ['address_check', 'employment_check', 'education_check'];
                const extraTypes = [...new Set((viewCase?.checks || [])
                  .map(c => typeof c.check_type === 'object' ? c.check_type.check_type : c.check_type)
                  .filter(ct => ct && !standardTypes.includes(ct)))];
                return extraTypes.map(ct => {
                  const checksOfType = (viewCase?.checks || []).filter(c => {
                    const t = typeof c.check_type === 'object' ? c.check_type.check_type : c.check_type;
                    return t === ct;
                  });
                  return (
                    <TabsContent key={ct} value={ct} className="m-0">
                      <div className="space-y-4">
                        <h3 className="font-semibold text-lg">{CHECK_NAMES[ct] || ct.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</h3>
                        {checksOfType.map((check, idx) => {
                          const checkId = check.check_id || check.id;
                          const subOption = check.sub_option ? ` (${check.sub_option.replace(/_/g, ' ')})` : '';
                          const insuffStatus = getInsuffStatus(viewCase.id, checkId);
                          return (
                            <Card key={idx} className="border">
                              <CardContent className="p-4 space-y-3">
                                <div className="flex justify-between items-center">
                                  <div>
                                    <p className="font-medium">{CHECK_NAMES[ct] || ct}{subOption}</p>
                                    <p className="text-xs text-slate-500">Check ID: {checkId}</p>
                                  </div>
                                  <Badge variant="outline" className="text-xs">{check.status || 'pending'}</Badge>
                                </div>
                                <div className="grid grid-cols-2 gap-3 text-sm">
                                  <div className="bg-slate-50 p-2 rounded">
                                    <p className="text-xs text-slate-500">Assigned To</p>
                                    <p className="font-medium">{check.assigned_to_name || check.assigned_to || '-'}</p>
                                  </div>
                                  <div className="bg-slate-50 p-2 rounded">
                                    <p className="text-xs text-slate-500">Verification Progress</p>
                                    <p className="font-medium">{check.verification_progress || '-'}</p>
                                  </div>
                                  {check.overall_result && (
                                    <div className="bg-slate-50 p-2 rounded">
                                      <p className="text-xs text-slate-500">Result</p>
                                      <p className="font-medium">{check.overall_result}</p>
                                    </div>
                                  )}
                                  {check.assigned_vendor && (
                                    <div className="bg-slate-50 p-2 rounded">
                                      <p className="text-xs text-slate-500">Vendor</p>
                                      <p className="font-medium">{check.assigned_vendor.vendor_name || '-'}</p>
                                    </div>
                                  )}
                                </div>
                                {insuffStatus && (
                                  <div className="bg-amber-50 border border-amber-200 rounded p-2">
                                    <p className="text-xs text-amber-700">Insufficiency: {insuffStatus}</p>
                                  </div>
                                )}
                              </CardContent>
                            </Card>
                          );
                        })}
                      </div>
                    </TabsContent>
                  );
                });
              })()}

              {/* Checks Tab - All checks with approval actions */}
              <TabsContent value="checks" className="m-0">
                <div className="space-y-3">
                  {viewCase?.checks?.map((c, i) => {
                    const insuffStatus = getInsuffStatus(viewCase.id, c.check_id);
                    const hasInsuff = hasOpenInsuff(viewCase.id, c.check_id);
                    const docs = checkDocuments[c.check_id] || [];
                    
                    return (
                      <div key={i} className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                              <ClipboardCheck className="w-5 h-5 text-purple-600" />
                            </div>
                            <div>
                              <span className="font-semibold text-slate-800">{CHECK_NAMES[c.check_type] || c.check_type}</span>
                              <p className="text-xs text-slate-500 font-mono">#{c.check_id}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge className={docs.length > 0 ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-500'}>
                              <FileText className="w-3 h-3 mr-1" />{docs.length} docs
                            </Badge>
                            {insuffStatus === 'waiting' ? (
                              <Badge className="bg-amber-100 text-amber-700">
                                <Clock className="w-3 h-3 mr-1" />Waiting Clearance
                              </Badge>
                            ) : insuffStatus === 'open' ? (
                              <Badge className="bg-rose-100 text-rose-700">
                                <AlertTriangle className="w-3 h-3 mr-1" />Insufficiency
                              </Badge>
                            ) : (
                              <Badge className="bg-emerald-100 text-emerald-700">
                                <CheckCircle className="w-3 h-3 mr-1" />Clear
                              </Badge>
                            )}
                          </div>
                        </div>
                        <div className="flex justify-between items-center">
                          <div className="flex gap-2">
                            <Button size="sm" variant="outline" onClick={() => openCheckDetails(viewCase, c)}>
                              <Eye className="w-4 h-4 mr-1" />View Details
                            </Button>
                            {docs.length > 0 && (
                              <Button size="sm" variant="outline" onClick={() => setActiveReviewTab('documents')}>
                                <FileText className="w-4 h-4 mr-1" />View Documents
                              </Button>
                            )}
                          </div>
                          <div className="flex gap-2">
                            {!hasInsuff ? (
                              <>
                                <Button 
                                  size="sm" 
                                  variant="outline" 
                                  className="text-rose-600 border-rose-200 hover:bg-rose-50"
                                  onClick={() => { setViewCase(null); setShowInsuffDialog({ caseId: viewCase.id, checkId: c.check_id }); }}
                                  disabled={viewCase?.status === 'pending_client_approval'}
                                  title={viewCase?.status === 'pending_client_approval' ? 'Pending client approval' : 'Raise insufficiency'}
                                >
                                  <AlertTriangle className="w-4 h-4 mr-1" />Raise Insuff
                                </Button>
                                <Button 
                                  size="sm" 
                                  className="bg-emerald-600 hover:bg-emerald-700"
                                  onClick={() => { setViewCase(null); setShowApproveDialog({ caseId: viewCase.id, checkId: c.check_id, hasInsuff: false }); }}
                                  disabled={viewCase?.status === 'pending_client_approval'}
                                  title={viewCase?.status === 'pending_client_approval' ? 'Pending client approval' : 'Approve and assign'}
                                >
                                  <CheckCircle className="w-4 h-4 mr-1" />Approve & Assign
                                </Button>
                              </>
                            ) : insuffStatus === 'waiting' ? (
                              <Button 
                                size="sm" 
                                className="bg-emerald-600 hover:bg-emerald-700"
                                onClick={() => { handleClearInsufficiency(viewCase.id, c.check_id); }}
                                disabled={viewCase?.status === 'pending_client_approval'}
                              >
                                <CheckCircle className="w-4 h-4 mr-1" />Clear Insufficiency
                              </Button>
                            ) : (
                              <Button 
                                size="sm" 
                                variant="outline"
                                className="text-amber-600 border-amber-200 hover:bg-amber-50"
                                onClick={() => { setViewCase(null); setShowApproveDialog({ caseId: viewCase.id, checkId: c.check_id, hasInsuff: true }); }}
                                disabled={viewCase?.status === 'pending_client_approval'}
                                title={viewCase?.status === 'pending_client_approval' ? 'Pending client approval' : 'Bypass and approve'}
                              >
                                Bypass & Approve
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {(!viewCase?.checks || viewCase.checks.length === 0) && (
                    <div className="text-center py-8 text-slate-500">
                      <ClipboardCheck className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                      <p>No checks found for this case</p>
                    </div>
                  )}
                </div>
              </TabsContent>
            </div>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Document Preview Dialog */}
      <Dialog open={!!previewDocument} onOpenChange={(o) => { if (!o) setPreviewDocument(null); }}>
        <DialogContent className="max-w-3xl max-h-[85vh]">
          <DialogHeader>
            <DialogTitle>{previewDocument?.original_filename || previewDocument?.document_type || 'Document Preview'}</DialogTitle>
          </DialogHeader>
          <div className="flex justify-center items-center min-h-[400px] bg-slate-100 rounded-lg">
            {previewDocument?.file_url ? (
              previewDocument.file_type?.includes('image') ? (
                <img 
                  src={previewDocument.file_url} 
                  alt={previewDocument.original_filename || 'Document'} 
                  className="max-w-full max-h-[60vh] object-contain"
                />
              ) : previewDocument.file_type?.includes('pdf') ? (
                <iframe 
                  src={previewDocument.file_url} 
                  className="w-full h-[60vh]"
                  title={previewDocument.original_filename || 'PDF Document'}
                />
              ) : (
                <div className="text-center p-8">
                  <FileText className="w-16 h-16 text-slate-400 mx-auto mb-4" />
                  <p className="text-slate-600 mb-4">Preview not available for this file type</p>
                  <Button onClick={() => window.open(previewDocument.file_url, '_blank')}>
                    <Download className="w-4 h-4 mr-2" />Download File
                  </Button>
                </div>
              )
            ) : (
              <div className="text-center p-8">
                <FileText className="w-16 h-16 text-slate-400 mx-auto mb-4" />
                <p className="text-slate-600">No preview available</p>
              </div>
            )}
          </div>
          <DialogFooter>
            {previewDocument?.file_url && (
              <Button variant="outline" onClick={() => window.open(previewDocument.file_url, '_blank')}>
                <ExternalLink className="w-4 h-4 mr-2" />Open in New Tab
              </Button>
            )}
            <Button onClick={() => setPreviewDocument(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Raise Insufficiency Dialog */}
      <Dialog open={!!showInsuffDialog} onOpenChange={(o) => { if (!o) setShowInsuffDialog(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Raise Insufficiency</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Reason</Label>
              <Select value={insuffForm.reason} onValueChange={(v) => setInsuffForm({...insuffForm, reason: v})}>
                <SelectTrigger><SelectValue placeholder="Select reason" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="missing_document">Missing Document</SelectItem>
                  <SelectItem value="unclear_document">Unclear/Illegible Document</SelectItem>
                  <SelectItem value="incorrect_data">Incorrect Data</SelectItem>
                  <SelectItem value="incomplete_data">Incomplete Data</SelectItem>
                  <SelectItem value="mismatch">Data Mismatch</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Description</Label>
              <Textarea 
                placeholder="Describe the insufficiency in detail..."
                value={insuffForm.description}
                onChange={(e) => setInsuffForm({...insuffForm, description: e.target.value})}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInsuffDialog(null)}>Cancel</Button>
            <Button className="bg-rose-600 hover:bg-rose-700" onClick={handleRaiseInsufficiency}>Raise Insufficiency</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Approve & Assign Dialog */}
      <Dialog open={!!showApproveDialog} onOpenChange={(o) => { if (!o) setShowApproveDialog(null); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Approve Check & Assign Agent</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Quick Data Review Section */}
            <div className="bg-slate-50 p-4 rounded-lg">
              <h4 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                <ClipboardCheck className="w-4 h-4" />Quick Data Review
              </h4>
              {viewEmployee ? (
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Name:</span>
                    <span className={viewEmployee.name ? 'text-slate-800' : 'text-rose-500'}>{viewEmployee.name || 'Missing'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Email:</span>
                    <span className={viewEmployee.email ? 'text-slate-800' : 'text-rose-500'}>{viewEmployee.email || 'Missing'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Phone:</span>
                    <span className={viewEmployee.phone ? 'text-slate-800' : 'text-rose-500'}>{viewEmployee.phone || 'Missing'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">PAN:</span>
                    <span className={viewEmployee.pan_number ? 'text-slate-800 font-mono' : 'text-rose-500'}>{viewEmployee.pan_number || 'Missing'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Aadhar:</span>
                    <span className={viewEmployee.aadhar_number ? 'text-slate-800 font-mono' : 'text-rose-500'}>{viewEmployee.aadhar_number || 'Missing'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Category:</span>
                    <Badge variant="outline" className={
                      viewEmployee.employee_category === 'fresher' ? 'bg-green-50 text-green-700 border-green-300' :
                      viewEmployee.employee_category === 'intern' ? 'bg-purple-50 text-purple-700 border-purple-300' :
                      viewEmployee.employee_category === 'trainee' ? 'bg-orange-50 text-orange-700 border-orange-300' :
                      'bg-blue-50 text-blue-700 border-blue-300'
                    }>
                      {(viewEmployee.employee_category || (viewEmployee.is_fresher ? 'fresher' : 'experienced')).charAt(0).toUpperCase() + (viewEmployee.employee_category || (viewEmployee.is_fresher ? 'fresher' : 'experienced')).slice(1)}
                    </Badge>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Employment:</span>
                    <span className={viewEmployee.employment_entries?.length > 0 ? 'text-emerald-600' : (viewEmployee.employee_category !== 'experienced' && (viewEmployee.employee_category || viewEmployee.is_fresher)) ? 'text-slate-600' : 'text-rose-500'}>
                      {viewEmployee.employment_entries?.length > 0 ? `${viewEmployee.employment_entries.length} record(s)` : (viewEmployee.employee_category !== 'experienced' && (viewEmployee.employee_category || viewEmployee.is_fresher)) ? 'N/A' : 'Missing'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Education:</span>
                    <span className={viewEmployee.education_entries?.length > 0 ? 'text-emerald-600' : 'text-rose-500'}>
                      {viewEmployee.education_entries?.length > 0 ? `${viewEmployee.education_entries.length} record(s)` : 'Missing'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">References:</span>
                    <span className={(viewEmployee.personal_references?.length > 0 || viewEmployee.professional_references?.length > 0) ? 'text-emerald-600' : 'text-rose-500'}>
                      {(viewEmployee.personal_references?.length || 0) + (viewEmployee.professional_references?.length || 0)} provided
                    </span>
                  </div>
                </div>
              ) : (
                <p className="text-slate-500 text-sm">Loading employee data...</p>
              )}
            </div>

            {/* Insufficiency Summary */}
            {(() => {
              const allInsuffs = getCombinedInsufficiencies();
              const pendingInsuffs = allInsuffs.filter(i => i.status !== 'cleared');
              const docInsuffs = pendingInsuffs.filter(i => i.isDocumentIssue);
              const dataInsuffs = pendingInsuffs.filter(i => !i.isDocumentIssue);
              
              if (pendingInsuffs.length > 0) {
                return (
                  <div className="space-y-3">
                    {/* Data Issues */}
                    {dataInsuffs.length > 0 && (
                      <div className="bg-rose-50 border border-rose-200 p-4 rounded-lg">
                        <h4 className="font-semibold text-rose-700 mb-2 flex items-center gap-2">
                          <AlertTriangle className="w-4 h-4" />
                          {dataInsuffs.length} Missing Data Items
                        </h4>
                        <div className="space-y-1 max-h-24 overflow-y-auto">
                          {dataInsuffs.slice(0, 4).map((insuff, idx) => (
                            <div key={idx} className="text-sm text-rose-600 flex items-center gap-2">
                              <span className="w-1.5 h-1.5 bg-rose-500 rounded-full"></span>
                              <span>{insuff.section}: {insuff.field}</span>
                            </div>
                          ))}
                          {dataInsuffs.length > 4 && (
                            <p className="text-xs text-rose-500 mt-1">...and {dataInsuffs.length - 4} more</p>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {/* Document Issues */}
                    {docInsuffs.length > 0 && (
                      <div className="bg-amber-50 border border-amber-200 p-4 rounded-lg">
                        <h4 className="font-semibold text-amber-700 mb-2 flex items-center gap-2">
                          <FileText className="w-4 h-4" />
                          {docInsuffs.length} Missing Documents
                        </h4>
                        <div className="space-y-1 max-h-24 overflow-y-auto">
                          {docInsuffs.slice(0, 4).map((insuff, idx) => (
                            <div key={idx} className="text-sm text-amber-600 flex items-center gap-2">
                              <span className="w-1.5 h-1.5 bg-amber-500 rounded-full"></span>
                              <span>{insuff.section}: {insuff.field}</span>
                            </div>
                          ))}
                          {docInsuffs.length > 4 && (
                            <p className="text-xs text-amber-500 mt-1">...and {docInsuffs.length - 4} more</p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              }
              return (
                <div className="bg-emerald-50 border border-emerald-200 p-3 rounded-lg">
                  <p className="text-emerald-700 text-sm flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    All data & documents complete - No insufficiencies
                  </p>
                </div>
              );
            })()}

            {showApproveDialog?.hasInsuff && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-amber-700 font-medium">Warning: This check has open insufficiencies</p>
                <label className="flex items-center gap-2 mt-2">
                  <input 
                    type="checkbox" 
                    checked={approveForm.bypassInsuff}
                    onChange={(e) => setApproveForm({...approveForm, bypassInsuff: e.target.checked})}
                  />
                  <span className="text-sm">Bypass insufficiency and approve anyway</span>
                </label>
              </div>
            )}
            <div>
              <Label>Assign to Agent</Label>
              <Select value={approveForm.assignTo} onValueChange={(v) => setApproveForm({...approveForm, assignTo: v})}>
                <SelectTrigger><SelectValue placeholder="Select agent" /></SelectTrigger>
                <SelectContent>
                  {agents.map(agent => (
                    <SelectItem key={agent.id} value={agent.id}>{agent.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowApproveDialog(null)}>Cancel</Button>
            <Button 
              className="bg-emerald-600 hover:bg-emerald-700" 
              onClick={handleApproveCheck}
              disabled={showApproveDialog?.hasInsuff && !approveForm.bypassInsuff}
            >
              {showApproveDialog?.hasInsuff ? 'Bypass & Approve' : 'Approve & Assign'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Update Status Dialog */}
      <Dialog open={!!showBulkUpdateDialog} onOpenChange={(o) => { if (!o) setShowBulkUpdateDialog(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardCheck className="w-5 h-5 text-emerald-600" />
              Bulk Update Check Status
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
              <p className="text-sm text-blue-800">
                <strong>{getSelectedCount(showBulkUpdateDialog)}</strong> check(s) selected for update
              </p>
              <p className="text-xs text-blue-600 mt-1">
                This will update all selected checks to the chosen status in one click.
              </p>
            </div>
            
            <div className="space-y-2">
              <Label>New Status</Label>
              <Select value={bulkUpdateStatus} onValueChange={setBulkUpdateStatus}>
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="verified">
                    <span className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-emerald-600" />
                      Verified
                    </span>
                  </SelectItem>
                  <SelectItem value="in_progress">
                    <span className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-blue-600" />
                      In Progress
                    </span>
                  </SelectItem>
                  <SelectItem value="failed">
                    <span className="flex items-center gap-2">
                      <XCircle className="w-4 h-4 text-red-600" />
                      Failed
                    </span>
                  </SelectItem>
                  <SelectItem value="not_applicable">
                    <span className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-slate-600" />
                      Not Applicable
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Remarks (Optional)</Label>
              <Textarea 
                value={bulkUpdateRemarks}
                onChange={(e) => setBulkUpdateRemarks(e.target.value)}
                placeholder="Add remarks for this bulk update..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBulkUpdateDialog(null)} disabled={bulkUpdating}>
              Cancel
            </Button>
            <Button 
              className="bg-emerald-600 hover:bg-emerald-700" 
              onClick={() => handleBulkStatusUpdate(showBulkUpdateDialog)}
              disabled={bulkUpdating || getSelectedCount(showBulkUpdateDialog) === 0}
            >
              {bulkUpdating ? (
                <>
                  <Clock className="w-4 h-4 mr-2 animate-spin" />
                  Updating...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Update {getSelectedCount(showBulkUpdateDialog)} Check(s)
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default QCCasesTab;
